import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Spin, Alert } from 'antd';
import { fetchHistoryData } from '../services/chartDataService';
import { ChartDataTransformer } from '../utils/chartDataTransformer';
import { ChartOptionsFactory } from '../utils/chartOptionsFactory';
import type { HistoryChartProps } from '../types/chart.types';
import type { ChartDataResponse } from '../types/chart.types';

const CHART_HEIGHT = 600;
const CHART_HEIGHT_MOBILE = 400;

/**
 * 過去データチャートコンポーネント
 */
export const HistoryChart: React.FC<HistoryChartProps> = ({
  panelId,
  selectedDataTypes,
  startDate,
  endDate,
}) => {
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // モバイル判定
  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // データ取得関数
  const fetchData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchHistoryData({
        panelId,
        startDate,
        endDate,
        dataTypes: selectedDataTypes,
      });
      setChartData(data);
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'データ取得に失敗しました';
      setError(errorMessage);
      setLoading(false);
    }
  }, [panelId, selectedDataTypes, startDate, endDate]);

  // データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ローディング表示
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT,
        }}
      >
        <Spin size="large" tip="データ読み込み中..." />
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <Alert
        message="エラー"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  // データなし
  if (!chartData || ChartDataTransformer.isEmpty(chartData.dataPoints)) {
    return (
      <Alert
        message="データなし"
        description="指定された期間にデータがありません"
        type="info"
        showIcon
        className="mb-4"
      />
    );
  }

  // EChartsシリーズデータに変換
  const transformResult = ChartDataTransformer.transformToEChartsSeries({
    dataPoints: chartData.dataPoints,
    selectedDataTypes,
  });
  const seriesData = transformResult.series;

  // チャートオプション生成
  const chartOptions = ChartOptionsFactory.createHistoryChartOptions({ seriesData, isMobile });

  return (
    <ReactECharts
      option={chartOptions}
      style={{ height: isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT, width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};
